// Copyright (c) 2020 embyt GmbH. All rights reserved.
// Author: Roman Morawek

import * as React from "react";
import type { JSX } from "react";
import { connect } from "react-redux";
import { FormattedMessage, defineMessages, IntlContext, IntlShape } from "react-intl";
import { Button, Modal } from "react-bootstrap";
import InputNumber from "rc-input-number";

import { ModalPanel } from "states/guistates";
import { ActionType, IAction, IActionChangeInput, raiseAction } from "actions";
import { IState } from "states/states";

import { DataState } from "types";
import { Bus, IInput, InputType } from "shared/types/parameter";
import { formatTimestamp, isSet } from "shared/utils/utils";
import { toDropdownItems, makeCommandSeqNr } from "guiutils";
import Input from "model/input";

import DateInput from "view/comp/DateInput";
import SaveFeedback from "view/comp/SaveFeedback";
import Dropdown from "view/comp/Dropdown";

type IModalInputItemProps = {
  index: number | undefined;
  isOpen: boolean;
  dataState: DataState;
  oldValue: number | undefined;
  parameter: IInput | undefined;
  linxTimestamp: number;
  moduleStatus: boolean | undefined;
};

const mapStateToProps = (state: IState) => {
  const parameter =
    isSet(state.gui.modalInput) ? state.para.parameter!.input[state.gui.modalInput] : undefined;
  const oldValue = isSet(state.gui.modalInput) ? state.data.input[state.gui.modalInput] : undefined;
  const moduleIndex =
    parameter?.bus === Bus.MODULE ?
      state.para.parameter?.module?.findIndex((curItem) => curItem.id === parameter.module)
    : undefined;

  const props: IModalInputItemProps = {
    index: state.gui.modalInput,
    isOpen: state.gui.modal.panel === ModalPanel.INPUT && isSet(parameter),
    dataState: state.gui.modal.dataState,
    oldValue,
    parameter,
    linxTimestamp: state.data.timestamp,
    moduleStatus: isSet(moduleIndex) ? state.data.module[moduleIndex] : true,
  };
  return props;
};

interface IModalInputState {
  wasOpen: boolean;
  newValue: number;
}

interface IModalInputItemDispatch {
  onOkClick: (index: number, value: number) => void;
  onCancelClick: () => void;
}

const mapDispatchToProps = (dispatch: (action: IAction<any>) => void) => {
  const dispatchProps: IModalInputItemDispatch = {
    onOkClick: (index: number, value: number) => {
      const payload: IActionChangeInput = {
        commandSeqNr: makeCommandSeqNr(),
        index,
        value,
      };
      dispatch(raiseAction(ActionType.ACT_CHANGE_INPUT_REQ, payload));
    },
    onCancelClick: () => {
      dispatch(raiseAction(ActionType.ACT_MODAL_CANCEL));
    },
  };
  return dispatchProps;
};

const messages = defineMessages({
  on: {
    id: "On",
  },
  off: {
    id: "Off",
  },
  selectValue: {
    id: "Select value",
  },
});

class ModalInputItemComponent extends React.Component<
  IModalInputItemProps & IModalInputItemDispatch,
  IModalInputState
> {
  static contextType = IntlContext;
  declare context: IntlShape;

  state: IModalInputState = {
    wasOpen: false,
    newValue: this.props.oldValue ?? 0,
  };

  componentDidUpdate() {
    // check for changes in old value
    if (this.props.isOpen !== this.state.wasOpen) {
      this.setState({
        wasOpen: this.props.isOpen,
        newValue: this.props.oldValue ?? 0,
      });
    }
  }

  public render() {
    if (!this.props.isOpen || this.props.index === undefined) {
      return <></>; // avoid rendering of inexisting data
    }

    return (
      <Modal show={this.props.isOpen} onHide={this.props.onCancelClick}>
        <Modal.Header closeButton>
          <FormattedMessage id="Input Settings" />
        </Modal.Header>
        <Modal.Body>{this.renderSettings()}</Modal.Body>
        <Modal.Footer>
          <SaveFeedback dataState={this.props.dataState} />
          <Button
            id="cancelInput"
            variant="secondary"
            className="me-1"
            onClick={this.props.onCancelClick}
          >
            <FormattedMessage id="Cancel" />
          </Button>
          <Button
            id="saveInput"
            variant="primary"
            onClick={this.props.onOkClick.bind(this, this.props.index, this.state.newValue)}
            disabled={
              this.props.dataState === DataState.SAVE_REQ ||
              this.props.oldValue === this.state.newValue
            }
          >
            <FormattedMessage id="Save" />
          </Button>
        </Modal.Footer>
      </Modal>
    );
  }

  private renderSettings() {
    const oldInputModel = new Input(
      this.props.parameter!,
      this.props.oldValue,
      this.props.linxTimestamp,
      this.props.moduleStatus,
    );
    const newInputModel = new Input(
      this.props.parameter!,
      this.state.newValue,
      this.props.linxTimestamp,
      this.props.moduleStatus,
    );

    let inputControl: JSX.Element;
    let inputValue = newInputModel.getText();
    if (this.props.parameter!.type === InputType.SWITCH) {
      // boolean input
      const onOffFormatter = (value: number) =>
        value ? this.context.formatMessage(messages.on) : this.context.formatMessage(messages.off);
      const choices = toDropdownItems(
        [0, 1],
        "value",
        (_name, value) => {
          this.setState({ newValue: value });
        },
        onOffFormatter,
      );
      inputControl = (
        <Dropdown
          className="btn-group-sm"
          items={choices}
          curItem={onOffFormatter(this.state.newValue)}
          disabled={this.props.dataState === DataState.SAVE_REQ}
        />
      );
    } else if (this.props.parameter!.type === InputType.PUSHBUTTON) {
      inputControl = (
        <Button
          variant="secondary"
          onClick={this.props.onOkClick.bind(this, this.props.index ?? 0, 1)}
          disabled={this.props.dataState === DataState.SAVE_REQ}
        >
          <FormattedMessage id="Switch" />
        </Button>
      );
    } else if (
      this.props.parameter!.type === InputType.ANALOG &&
      (this.props.parameter!.unit === "date" || this.props.parameter!.unit === "timestamp")
    ) {
      const isDate = this.props.parameter!.unit === "date";
      inputControl = (
        <DateInput
          type={isDate ? "date" : "datetime-local"}
          disabled={this.props.dataState === DataState.SAVE_REQ}
          // if not set, we use the current time
          valueMs={
            newInputModel.rawValue ? (newInputModel.getValue() as Date).getTime() : Date.now()
          }
          onChange={(valueMs) => {
            const date = new Date(valueMs);
            this.setState({
              newValue:
                isDate ?
                  (date.getFullYear() - 1900) * 10000 + (date.getMonth() + 1) * 100 + date.getDate()
                  // timestamp is scaled in minutes since 2015, timestamp(1.1.2015) = 1420070400
                : Math.max(0, (valueMs / 1000 - 1420070400) / 60),
            });
          }}
        />
      );
    } else {
      // type == TYPE_ANALOG
      // various data types handled by html input field
      let inputType: string;
      if (this.props.parameter!.unit === "time") {
        inputType = "time";
        // if a date is not set, we use current timestamp
        if (newInputModel.rawValue === 0) {
          inputValue = formatTimestamp(Date.now());
        }
      } else {
        // default input type for all other units
        inputType = "number";
      }
      inputControl =
        inputType === "number" ?
          <>
            <InputNumber
              type={inputType}
              onChange={(value) => {
                if (isSet(value)) {
                  this.setState({ newValue: value });
                }
              }}
              disabled={this.props.dataState === DataState.SAVE_REQ}
              value={parseFloat(inputValue)}
            />
            {Boolean(this.props.parameter?.unit) && (
              <span className="ms-2">{this.props.parameter!.unit}</span>
            )}
          </>
        : <input
            type={inputType}
            onChange={(event) => {
              this.decodeValue(event?.currentTarget);
            }}
            step="any"
            disabled={this.props.dataState === DataState.SAVE_REQ}
            value={inputValue}
          />;
    }

    return (
      <>
        <p>
          <FormattedMessage id="Changing the settings of this input item:" />
        </p>
        <table>
          <tbody>
            {this.props.parameter!.type !== InputType.PUSHBUTTON && (
              <tr>
                <td>
                  <FormattedMessage id="Old value" />:
                </td>
                <td>{oldInputModel.getText()}</td>
              </tr>
            )}
            <tr>
              <td>
                <FormattedMessage id="New value" />:
              </td>
              <td>{inputControl}</td>
            </tr>
          </tbody>
        </table>
      </>
    );
  }

  private decodeValue(element: HTMLInputElement) {
    const type = this.props.parameter!.type;
    const unit = this.props.parameter!.unit;
    let value: number | undefined;

    // convert special data types
    if (type === InputType.ANALOG && unit === "time") {
      // data type time
      const time = element.valueAsNumber;
      if (!isNaN(time)) {
        value = Math.round(time / 1000);
      }
    } else {
      value = element.valueAsNumber;
    }

    this.setState({ newValue: value ?? 0 });
  }
}

const ModalInputItem = connect(mapStateToProps, mapDispatchToProps)(ModalInputItemComponent);

export default ModalInputItem;
